import { useState, useEffect, useMemo } from 'react';
import { getInvoices, getInvoicePdfUrl } from '@services/api/invoiceService';
import Toast from '@components/common/Toast';

/**
 * InvoicesList — Invoice PDF list with download/share
 *
 * Tabs: All | Pending | Paid | Overdue
 * Layout mirrors CasesList / OrdersList for portal consistency.
 * Invoices are PDFs dropped by external invoicing app into Azure Data Lake.
 * Simple display, download, share — no write operations from MRT.
 */

const TAB_CONFIG = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Pending', badgeClass: 'bg-warning text-carbon-black' },
  { key: 'paid', label: 'Paid', badgeClass: 'bg-success text-white' },
  { key: 'overdue', label: 'Overdue', badgeClass: 'bg-error text-white' },
];

function StatusBadge({ status }) {
  const config = TAB_CONFIG.find(t => t.key === status);
  if (!config || !config.badgeClass) return null;
  return (
    <span className={`inline-block px-2.5 py-0.5 text-xs ${config.badgeClass}`}>
      {config.label}
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

function formatCurrency(amount, currency = 'USD') {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
}

export default function InvoicesList() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [sortOrder, setSortOrder] = useState('newest');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [toast, setToast] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    getInvoices().then(data => {
      if (mounted) {
        setInvoices(data);
        setLoading(false);
      }
    });
    return () => { mounted = false; };
  }, []);

  const filtered = useMemo(() => {
    let result = activeTab === 'all'
      ? [...invoices]
      : invoices.filter(inv => inv.status === activeTab);

    if (dateFrom) {
      const from = new Date(dateFrom);
      result = result.filter(inv => new Date(inv.issueDate) >= from);
    }
    if (dateTo) {
      const to = new Date(dateTo);
      to.setHours(23, 59, 59, 999);
      result = result.filter(inv => new Date(inv.issueDate) <= to);
    }

    result.sort((a, b) => {
      const da = new Date(a.issueDate);
      const db = new Date(b.issueDate);
      return sortOrder === 'newest' ? db - da : da - db;
    });

    return result;
  }, [invoices, activeTab, sortOrder, dateFrom, dateTo]);

  const counts = useMemo(() => {
    const map = { all: invoices.length, pending: 0, paid: 0, overdue: 0 };
    invoices.forEach(inv => { if (map[inv.status] !== undefined) map[inv.status]++; });
    return map;
  }, [invoices]);

  const handleDownload = async (inv) => {
    try {
      const url = await getInvoicePdfUrl(inv.invoiceId);
      // In production this opens a signed Azure Blob URL
      // For stub, just log and show toast
      console.log('[InvoicesList] Downloading:', url);
      setToast({ type: 'success', message: `Downloading ${inv.fileName}` });
    } catch {
      setToast({ type: 'error', message: 'Failed to download invoice.' });
    }
  };

  const handleShare = async (inv) => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Invoice ${inv.invoiceNumber}`,
          text: `Invoice ${inv.invoiceNumber} for order ${inv.orderNumber}`,
        });
      } catch {
        // User cancelled share
      }
    } else {
      // Fallback: copy link
      setToast({ type: 'success', message: 'Invoice link copied to clipboard.' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-6 h-6 border-2 border-electric-blue border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Tabs */}
      <div className="border-b border-n2">
        <nav className="flex gap-6" role="tablist">
          {TAB_CONFIG.map(tab => (
            <button
              key={tab.key}
              role="tab"
              aria-selected={activeTab === tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`
                relative pb-3 text-sm font-medium transition-colors
                ${activeTab === tab.key
                  ? 'text-electric-blue'
                  : 'text-n5 hover:text-carbon-black'}
              `}
            >
              {tab.label}
              {counts[tab.key] > 0 && (
                <span className="ml-1.5 text-xs text-n5">({counts[tab.key]})</span>
              )}
              {activeTab === tab.key && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-electric-blue rounded-t" />
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Filters row */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <label className="text-xs text-n5">From</label>
          <input
            type="date"
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
            className="border border-n3 rounded px-2 py-1 text-xs focus:ring-1 focus:ring-electric-blue"
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-n5">To</label>
          <input
            type="date"
            value={dateTo}
            onChange={e => setDateTo(e.target.value)}
            className="border border-n3 rounded px-2 py-1 text-xs focus:ring-1 focus:ring-electric-blue"
          />
        </div>
        <select
          value={sortOrder}
          onChange={e => setSortOrder(e.target.value)}
          className="ml-auto border border-n3 rounded px-2 py-1 text-xs focus:ring-1 focus:ring-electric-blue"
        >
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
        </select>
      </div>

      {/* Invoice rows */}
      {filtered.length === 0 ? (
        <div className="text-center py-10 text-sm text-n5">
          No invoices found.
        </div>
      ) : (
        <ul className="divide-y divide-n2">
          {filtered.map(inv => (
            <li key={inv.invoiceId} className="py-4 px-2 hover:bg-n1 rounded transition-colors">
              <div className="flex items-start gap-4">
                {/* PDF icon */}
                <div className="flex-shrink-0 w-10 h-10 bg-error/10 rounded flex items-center justify-center">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#EB0D0D" strokeWidth="1.5">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                  </svg>
                </div>

                {/* Details */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm text-carbon-black">
                      {inv.invoiceNumber}
                    </span>
                    <StatusBadge status={inv.status} />
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-n5">
                    <span>Order {inv.orderNumber}</span>
                    <span>Issued {formatDate(inv.issueDate)}</span>
                    <span>Due {formatDate(inv.dueDate)}</span>
                    {inv.paidDate && <span>Paid {formatDate(inv.paidDate)}</span>}
                  </div>
                  <p className="text-sm text-carbon-black mt-1">
                    {formatCurrency(inv.amount, inv.currency)}
                  </p>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => handleDownload(inv)}
                    title="Download PDF"
                    className="p-2 text-n5 hover:text-electric-blue hover:bg-blue-light/30 rounded transition-colors"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleShare(inv)}
                    title="Share invoice"
                    className="p-2 text-n5 hover:text-electric-blue hover:bg-blue-light/30 rounded transition-colors"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="18" cy="5" r="3" />
                      <circle cx="6" cy="12" r="3" />
                      <circle cx="18" cy="19" r="3" />
                      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                    </svg>
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Toast */}
      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}
